import { useState, useEffect, useRef, useCallback } from 'react';
import { getSupabase } from '../lib/supabase';
import { VEHICLE_COLUMNS, mapSupabaseRowToVehicle, type Vehicle } from '../lib/vehicles';
import { safeQuery, logQueryTiming } from '../lib/queryUtils';
import { MOCK_VEHICLES } from '../mocks/mockVehicles';

const FETCH_TIMEOUT_MS = 8000;

// ─── SWR Cache ────────────────────────────────────────────────────────────────
// In-memory cache: survives route changes within the same session.
// Key: vehicle id/stock_number  Value: { vehicle, fetchedAt }
interface CacheEntry {
  vehicle: Vehicle;
  fetchedAt: number;
}
const svrCache = new Map<string, CacheEntry>();

/** How long (ms) a cached entry is considered "fresh" — no background refetch needed. */
const FRESH_TTL_MS = 60_000; // 1 minute

/** Fields that can change without a full page reload (price + availability). */
const LIVE_COLUMNS = 'id,price,status,updated_at' as const;

// ─── Request Deduplication Cache ──────────────────────────────────────────────
const pendingRequests = new Map<string, Promise<Vehicle | null>>();

function isUuidFormat(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useVehicleById(id: string | undefined) {
  const [vehicle, setVehicle] = useState<Vehicle | null>(() => {
    if (!id) return null;
    return svrCache.get(id)?.vehicle ?? null;
  });
  const [loading, setLoading] = useState(() => {
    if (!id) return false;
    return !svrCache.has(id);
  });
  const [error, setError] = useState<string | null>(null);
  /** True while a background revalidation is running (price/status refresh). */
  const [revalidating, setRevalidating] = useState(false);

  // Keep a ref so the background refresh closure always sees the latest vehicle
  const vehicleRef = useRef<Vehicle | null>(vehicle);
  vehicleRef.current = vehicle;

  // ── Background revalidation (price + status only) ─────────────────────────
  const revalidate = useCallback(async (vehicleId: string) => {
    if (!vehicleId || vehicleId.startsWith('mock-')) return;

    setRevalidating(true);
    try {
      const supabase = await getSupabase();
      const col = isUuidFormat(vehicleId) ? 'id' : 'stock_number';

      const { data, error: qErr } = await supabase
        .from('vehicles')
        .select(LIVE_COLUMNS)
        .eq(col, vehicleId)
        .limit(1);

      if (qErr || !data || data.length === 0) return;

      const fresh = data[0] as { id: string; price: number; status: string; updated_at: string };
      const current = vehicleRef.current;
      if (!current) return;

      // Only trigger a re-render if something actually changed
      const priceChanged = fresh.price !== current.price;
      const statusChanged = fresh.status !== current.status;

      if (priceChanged || statusChanged) {
        const updated: Vehicle = {
          ...current,
          price: fresh.price,
          status: fresh.status as Vehicle['status'],
        };
        svrCache.set(vehicleId, { vehicle: updated, fetchedAt: Date.now() });
        setVehicle(updated);
      }
    } catch {
      // Silent — background refresh failures never surface to the user
    } finally {
      setRevalidating(false);
    }
  }, []);

  // ── Full fetch (first load or cache miss) ─────────────────────────────────
  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError('No vehicle ID provided');
      return;
    }

    setError(null);

    // ── Serve stale cache immediately ─────────────────────────────────────
    const cached = svrCache.get(id);
    if (cached) {
      setVehicle(cached.vehicle);
      setLoading(false);

      const age = Date.now() - cached.fetchedAt;
      if (age > FRESH_TTL_MS) {
        // Stale — kick off a silent background refresh
        revalidate(id);
      }
      return;
    }

    // ── Mock fast-path ────────────────────────────────────────────────────
    const mockMatch = MOCK_VEHICLES.find(
      (v) => v.uuid === id || String(v.id) === id || v.stock_number === id
    );
    if (mockMatch) {
      svrCache.set(id, { vehicle: mockMatch, fetchedAt: Date.now() });
      setVehicle(mockMatch);
      setLoading(false);
      return;
    }

    setLoading(true);
    setVehicle(null);

    // ── Reuse in-flight request ───────────────────────────────────────────
    const existingRequest = pendingRequests.get(id);
    if (existingRequest) {
      existingRequest
        .then((result) => {
          if (result) svrCache.set(id, { vehicle: result, fetchedAt: Date.now() });
          setVehicle(result);
          setError(result ? null : 'Vehicle not found');
        })
        .catch(() => setError('Failed to load vehicle'))
        .finally(() => setLoading(false));
      return;
    }

    // ── Build & cache the fetch promise ───────────────────────────────────
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const fetchPromise = (async (): Promise<Vehicle | null> => {
      const startTime = Date.now();
      try {
        const supabase = await getSupabase();
        const signal = controller.signal;

        if (isUuidFormat(id)) {
          const result = await safeQuery(
            supabase
              .from('vehicles')
              .select(VEHICLE_COLUMNS)
              .eq('id', id)
              .limit(1)
              .abortSignal(signal),
            {
              operation: `fetch vehicle by UUID (${id})`,
              fallbackMessage: 'Unable to load vehicle details. Please try again.',
            }
          );
          clearTimeout(timeout);
          logQueryTiming('fetch vehicle by UUID', startTime);
          if (result.error) throw new Error(result.error.message);
          if (!result.data || result.data.length === 0) {
            return MOCK_VEHICLES.find((v) => v.uuid === id) ?? null;
          }
          return mapSupabaseRowToVehicle(result.data[0]);
        }

        // Parallel: UUID + stock_number
        const [byUuid, byStock] = await Promise.allSettled([
          safeQuery(
            supabase.from('vehicles').select(VEHICLE_COLUMNS).eq('id', id).limit(1).abortSignal(signal),
            { operation: `fetch vehicle by UUID (${id})` }
          ),
          safeQuery(
            supabase.from('vehicles').select(VEHICLE_COLUMNS).eq('stock_number', id).limit(1).abortSignal(signal),
            { operation: `fetch vehicle by stock_number (${id})` }
          ),
        ]);

        clearTimeout(timeout);
        logQueryTiming('fetch vehicle parallel', startTime);

        const uuidRow =
          byUuid.status === 'fulfilled' && !byUuid.value.error && byUuid.value.data?.length
            ? mapSupabaseRowToVehicle(byUuid.value.data[0])
            : null;
        const stockRow =
          byStock.status === 'fulfilled' && !byStock.value.error && byStock.value.data?.length
            ? mapSupabaseRowToVehicle(byStock.value.data[0])
            : null;

        const found = uuidRow ?? stockRow;
        if (found) return found;

        return MOCK_VEHICLES.find(
          (v) => v.uuid === id || String(v.id) === id || v.stock_number === id
        ) ?? null;
      } catch (err) {
        clearTimeout(timeout);
        const isAbort =
          (err instanceof DOMException && err.name === 'AbortError') ||
          (err instanceof Error && err.message.includes('AbortError')) ||
          (err && typeof err === 'object' && 'code' in err && (err as any).code === '20');
        if (isAbort) throw new Error('Request cancelled');
        if (import.meta.env.DEV) console.error('Vehicle fetch error:', err);
        throw err;
      } finally {
        pendingRequests.delete(id);
      }
    })();

    pendingRequests.set(id, fetchPromise);

    fetchPromise
      .then((result) => {
        if (result) svrCache.set(id, { vehicle: result, fetchedAt: Date.now() });
        setVehicle(result);
        setError(result ? null : 'Vehicle not found');
      })
      .catch(() => {
        const fallback = MOCK_VEHICLES.find(
          (v) => v.uuid === id || String(v.id) === id || v.stock_number === id
        );
        if (fallback) {
          svrCache.set(id, { vehicle: fallback, fetchedAt: Date.now() });
          setVehicle(fallback);
          setError(null);
        } else {
          setError('Failed to load vehicle');
        }
      })
      .finally(() => setLoading(false));

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [id, revalidate]);

  // ── Page visibility revalidation ──────────────────────────────────────────
  // When the user tabs back in, silently refresh price + status.
  useEffect(() => {
    if (!id) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && vehicleRef.current) {
        revalidate(id);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [id, revalidate]);

  return { vehicle, loading, error, revalidating };
}
